<template>
  <div class="image-format-picker">
    <!-- Slot is the actual image preview (left untouched) -->
    <slot></slot>

    <!-- Eye-icon trigger (top-right corner) -->
    <button
      type="button"
      ref="trigger"
      class="ifp-eye-btn"
      :class="{ 'ifp-eye-btn-active': open }"
      :aria-expanded="open ? 'true' : 'false'"
      :aria-haspopup="'menu'"
      :title="open ? 'Close' : 'View formats'"
      @click.stop="toggleOpen"
    >
      <i class="el-icon-view"></i>
    </button>

    <div v-if="badges.length" class="ifp-badges">
      <span v-for="badge in badges" :key="badge.label" class="ifp-badge">
        {{ badge.label }}
      </span>
    </div>

    <transition name="ifp-fade">
      <div
        v-if="open"
        ref="popover"
        class="ifp-popover"
        role="menu"
        @click.stop
      >
        <div
          v-for="(opt, idx) in viewOptions"
          :key="idx"
          class="ifp-popover-item"
          :class="{ 'ifp-popover-item-disabled': !opt.url }"
          :role="opt.url ? 'menuitem' : null"
          :tabindex="opt.url ? 0 : -1"
          @click="onItemClick(opt)"
          @keydown.enter.space.prevent="onItemClick(opt)"
        >
          <div class="ifp-popover-item-row">
            <span class="ifp-popover-item-label">{{ opt.label }}</span>
            <span v-if="opt.badge" class="ifp-badge ifp-popover-inline-badge">
              {{ opt.badge }}
            </span>
          </div>
          <div class="ifp-popover-item-meta">{{ opt.meta }}</div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
var IMAGE_EXTS = ['jpg', 'png', 'gif', 'webp', 'svg', 'bmp', 'avif']

var REAL_HOST_SUFFIXES = [
  '.amazonaws.com', // S3 — all URL styles, all regions, all buckets
  '.cloudfront.net', // CloudFront — all distributions
]

function extOfUrl(url) {
  if (!url || typeof url !== 'string') return null
  // Strip query/hash before extension extraction so signed URLs work.
  var clean = url.split('?')[0].split('#')[0]
  var match = clean.match(/\.([a-z0-9]+)$/i)
  if (!match) return null
  var ext = match[1].toLowerCase()
  if (ext === 'jpeg') ext = 'jpg'
  if (IMAGE_EXTS.indexOf(ext) === -1) return null
  return ext
}

function isRealUrl(url) {
  if (!url || typeof url !== 'string') return false

  var parsed
  try {
    parsed = new URL(url)
  } catch (e) {
    return false
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return false
  var host = parsed.hostname.toLowerCase()
  for (var i = 0; i < REAL_HOST_SUFFIXES.length; i++) {
    var suffix = REAL_HOST_SUFFIXES[i]
    var bareApex = suffix.charAt(0) === '.' ? suffix.slice(1) : suffix
    if (host === bareApex) return true
    if (host.length > suffix.length && host.slice(-suffix.length) === suffix)
      return true
  }
  return false
}

export default {
  name: 'ImageFormatPicker',
  props: {
    link: {
      // Object with {small,medium,large,source,webp?} OR a single URL string
      // OR null/undefined.
      required: false,
      default: null,
    },
    supportsWebp: {
      type: Boolean,
      default: false,
    },
  },
  data: function () {
    return {
      open: false,
    }
  },
  computed: {
    // The "main" image URL — prefer source, fall back through size variants.
    primaryUrl: function () {
      if (!this.link) return null
      if (typeof this.link === 'string') {
        return isRealUrl(this.link) ? this.link : null
      }
      var candidates = [
        this.link.source,
        this.link.large,
        this.link.medium,
        this.link.small,
      ]
      for (var i = 0; i < candidates.length; i++) {
        if (isRealUrl(candidates[i])) return candidates[i]
      }
      return null
    },
    webpUrl: function () {
      if (!this.link || typeof this.link === 'string') return null
      return isRealUrl(this.link.webp) ? this.link.webp : null
    },
    primaryExt: function () {
      return extOfUrl(this.primaryUrl)
    },

    badges: function () {
      var seen = Object.create(null)
      var list = []

      var pushExt = function (url) {
        var ext = extOfUrl(url)
        if (!ext) return
        if (!isRealUrl(url)) return // exclude dummy placeholders
        if (seen[ext]) return
        seen[ext] = true
        list.push({
          label: ext === 'webp' ? 'WebP' : ext.toUpperCase(),
          kind: ext === 'webp' ? 'webp' : 'format',
        })
      }

      if (typeof this.link === 'string') {
        pushExt(this.link)
      } else if (this.link) {
        ;['small', 'medium', 'large', 'source', 'webp'].forEach(
          function (k) {
            pushExt(this.link[k])
          }.bind(this),
        )
      }

      // WebP badge always sorted last for stable visual order.
      list.sort(function (a, b) {
        if (a.kind === b.kind) return 0
        return a.kind === 'webp' ? 1 : -1
      })
      return list
    },

    viewOptions: function () {
      var opts = []

      if (!this.primaryUrl) {
        opts.push({
          label: 'No image uploaded',
          meta: 'Upload an image to enable preview',
          url: null,
          badge: null,
        })
        return opts
      }

      var hasMulti = this.link && typeof this.link === 'object'

      if (!hasMulti) {
        opts.push({
          label: 'Open Image',
          meta: 'Opens in a new tab',
          url: this.primaryUrl,
          badge: this.primaryExt ? this.primaryExt.toUpperCase() : null,
        })
        return opts
      }

      opts.push({
        label: 'Original',
        meta: 'Opens in a new tab',
        url: this.primaryUrl,
        badge: this.primaryExt ? this.primaryExt.toUpperCase() : null,
      })

      if (this.supportsWebp) {
        if (this.webpUrl) {
          opts.push({
            label: 'WebP',
            meta: 'Opens in a new tab',
            url: this.webpUrl,
            badge: 'WebP',
          })
        } else {
          opts.push({
            label: 'WebP',
            meta: 'Not generated yet',
            url: null,
            badge: null,
          })
        }
      }

      return opts
    },
  },

  methods: {
    toggleOpen: function () {
      this.open = !this.open
    },
    closePopover: function () {
      this.open = false
    },
    onItemClick: function (opt) {
      if (!opt.url) return
      window.open(opt.url, '_blank', 'noopener,noreferrer')
      this.closePopover()
    },
    onDocumentClick: function (e) {
      if (!this.open) return
      var pop = this.$refs.popover
      var trig = this.$refs.trigger
      if (pop && pop.contains(e.target)) return
      if (trig && trig.contains(e.target)) return
      this.closePopover()
    },
    onKeydown: function (e) {
      if (!this.open) return
      if (e.key === 'Escape' || e.keyCode === 27) {
        this.closePopover()
        if (this.$refs.trigger) this.$refs.trigger.focus()
      }
    },
  },

  mounted: function () {
    document.addEventListener('click', this.onDocumentClick)
    document.addEventListener('keydown', this.onKeydown)
  },
  beforeDestroy: function () {
    document.removeEventListener('click', this.onDocumentClick)
    document.removeEventListener('keydown', this.onKeydown)
  },
}
</script>

<style scoped>
.image-format-picker {
  position: relative;
  display: inline-block;
  vertical-align: top;
  line-height: 0;
}

/* ─── Eye icon button ─── */
.ifp-eye-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  outline: none;
  background: rgba(0, 0, 0, 0.65);
  color: #ffffff;
  border-radius: 50%;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  padding: 0;
  z-index: 6;
  transition: background 0.15s ease, transform 0.1s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
}
.ifp-eye-btn:hover,
.ifp-eye-btn:focus {
  background: rgba(0, 0, 0, 0.85);
}
.ifp-eye-btn:focus-visible {
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.5), 0 1px 3px rgba(0, 0, 0, 0.25);
}
.ifp-eye-btn-active {
  background: rgba(0, 0, 0, 0.92);
  transform: scale(0.95);
}

/* ─── Format badges ─── */
.ifp-badges {
  position: absolute;
  bottom: 8px;
  left: 8px;
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  z-index: 5;
  pointer-events: none;
  line-height: 1.5;
}
.ifp-badge {
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.4px;
  padding: 2px 7px;
  border-radius: 10px;
  line-height: 1.5;
  text-transform: uppercase;
  white-space: nowrap;
  background: rgba(73, 80, 87, 0.92);
  color: #ffffff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.18);
}

/* ─── Popover ─── */
.ifp-popover {
  position: absolute;
  top: 44px;
  right: 0;
  width: 160px;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.15);
  z-index: 50;
  overflow: hidden;
  line-height: 1.4;
}

/* Tiny arrow pointing up at the eye icon */
.ifp-popover::before {
  content: '';
  position: absolute;
  top: -6px;
  right: 12px;
  width: 10px;
  height: 10px;
  background: #ffffff;
  border-left: 1px solid #e0e0e0;
  border-top: 1px solid #e0e0e0;
  transform: rotate(45deg);
}

.ifp-popover-item {
  padding: 10px 14px;
  cursor: pointer;
  transition: background 0.1s ease;
  border-bottom: 1px solid #f1f1f1;
  user-select: none;
}
.ifp-popover-item:last-child {
  border-bottom: none;
}
.ifp-popover-item:hover,
.ifp-popover-item:focus {
  background: #f6f8fa;
  outline: none;
}
.ifp-popover-item-disabled {
  cursor: default;
  opacity: 0.55;
}
.ifp-popover-item-disabled:hover {
  background: transparent;
}

.ifp-popover-item-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.ifp-popover-item-label {
  font-weight: 600;
  font-size: 13px;
  color: #27282a;
}

.ifp-popover-inline-badge {
  font-size: 9px;
  padding: 1px 6px;
  background: #e9ecef !important;
  color: #495057 !important;
  box-shadow: none !important;
}
.ifp-popover-item-meta {
  font-size: 11px;
  color: #6c757d;
  margin-top: 2px;
  line-height: 1.35;
}

/* ─── Transitions ─── */
.ifp-fade-enter-active,
.ifp-fade-leave-active {
  transition: opacity 0.12s ease, transform 0.12s ease;
}
.ifp-fade-enter,
.ifp-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
